import { useId, type ReactNode } from 'react';
import clsx from 'clsx';
import './Checkbox.css';

export interface RadioOption<V extends string> {
  value: V;
  label: ReactNode;
  disabled?: boolean;
}

export interface RadioGroupProps<V extends string> {
  name?: string;
  legend?: ReactNode;
  options: RadioOption<V>[];
  value: V | null;
  onChange: (next: V) => void;
  orientation?: 'vertical' | 'horizontal';
  error?: boolean;
  disabled?: boolean;
  className?: string;
}

/**
 * Controlled radio group — pick exactly one from a typed set.
 * Renders a <fieldset> / <legend> for accessibility.
 */
export function RadioGroup<V extends string>({
  name,
  legend,
  options,
  value,
  onChange,
  orientation = 'vertical',
  error,
  disabled,
  className,
}: RadioGroupProps<V>) {
  const autoName = useId();
  const groupName = name ?? autoName;

  return (
    <fieldset
      className={clsx('fluent-radio-group', className)}
      data-orientation={orientation}
      disabled={disabled || undefined}
    >
      {legend != null && <legend className="fluent-radio-group__legend">{legend}</legend>}
      {options.map((opt) => {
        const optId = `${groupName}-${opt.value}`;
        const isDisabled = disabled || opt.disabled;
        const isChecked = value === opt.value;
        return (
          <label
            key={opt.value}
            htmlFor={optId}
            className="fluent-choice"
            data-disabled={isDisabled || undefined}
            data-invalid={error || undefined}
          >
            <input
              id={optId}
              type="radio"
              name={groupName}
              value={opt.value}
              checked={isChecked}
              disabled={isDisabled}
              className="fluent-choice__native"
              onChange={() => onChange(opt.value)}
              aria-invalid={error || undefined}
            />
            <span className="fluent-choice__dot" aria-hidden="true" />
            <span className="fluent-choice__label">{opt.label}</span>
          </label>
        );
      })}
    </fieldset>
  );
}
